// RunHistoryPanel — UI-panel som listar user:s senaste runs för ett
// specifikt workflow. Använder `list_my_recent_runs`-RPC (P-001).
//
// Props:
//   workflowId: UUID — panel filtrerar runs för detta workflow.
//   onTriggerRun?: () => void — "Kör nu"-knapp-callback. Visas bara
//     för Enterprise-users (canRunProcesses).
//
// Status-badges: mapping till semantic color per status. Duration
// visas i ms/s/m beroende på storlek.

import { useEffect, useState, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  RefreshCw, Play, CheckCircle2, XCircle, Clock, Ban, Loader2,
  Webhook, Calendar, Code, MousePointerClick, FlaskConical, RotateCcw,
} from 'lucide-react';
import { listMyRecentRuns, getFailedRunForRetry, type RunRow, type RunStatus, type TriggerKind } from '@/lib/runHistory';
import { triggerWorkflowRun } from '@/lib/runTrigger';
import { usePlan } from '@/hooks/usePlan';
import { toast } from '@/hooks/use-toast';
import { RunDetailDialog } from './RunDetailDialog';

interface Props {
  workflowId: string;
  onTriggerRun?: () => void;
}

const STATUS_STYLES: Record<RunStatus, { label: string; variant: 'default' | 'destructive' | 'secondary' | 'outline'; icon: typeof CheckCircle2 }> = {
  succeeded: { label: 'Lyckades', variant: 'default', icon: CheckCircle2 },
  failed: { label: 'Misslyckades', variant: 'destructive', icon: XCircle },
  running: { label: 'Kör', variant: 'secondary', icon: Loader2 },
  pending: { label: 'Väntar', variant: 'outline', icon: Clock },
  cancelled: { label: 'Avbruten', variant: 'outline', icon: Ban },
};

const TRIGGER_ICONS: Record<TriggerKind, typeof Code> = {
  manual: MousePointerClick,
  webhook: Webhook,
  scheduled: Calendar,
  api: Code,
  test: FlaskConical,
};

function formatDuration(start: string, end: string | null): string {
  if (!end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'nyss';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)} min sedan`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h sedan`;
  return `${Math.floor(diff / 86400_000)}d sedan`;
}

export function RunHistoryPanel({ workflowId, onTriggerRun }: Props) {
  const { limits } = usePlan();
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailRunId, setDetailRunId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const result = await listMyRecentRuns(workflowId, 25);
    if (!result.success) {
      setError(result.error ?? 'load-failed');
    } else {
      setRuns(result.runs);
    }
    setIsLoading(false);
  }, [workflowId]);

  useEffect(() => { void load(); }, [load]);

  const canRun = limits.canRunProcesses;

  const handleRetry = async (runId: string) => {
    const fetched = await getFailedRunForRetry(runId);
    if (!fetched.success || !fetched.workflow_id) {
      toast({
        title: 'Kunde inte hämta run-data',
        description: fetched.error,
        variant: 'destructive',
      });
      return;
    }
    const triggered = await triggerWorkflowRun({
      workflowId: fetched.workflow_id,
      triggerData: fetched.trigger_data,
      triggerKind: 'manual',
    });
    if (triggered.success) {
      toast({
        title: 'Retry startad',
        description: `run_id: ${triggered.runId?.slice(0, 8)} — status: ${triggered.status}`,
      });
      void load();
    } else {
      toast({
        title: 'Retry misslyckades',
        description: triggered.error,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="run-history-panel">
      <div className="flex items-center justify-between p-3 border-b">
        <div>
          <h3 className="text-sm font-semibold">Run-historik</h3>
          <p className="text-xs text-muted-foreground">
            Senaste 25 körningarna för detta workflow
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={isLoading}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
            Uppdatera
          </Button>
          {canRun && onTriggerRun && (
            <Button size="sm" onClick={onTriggerRun} data-testid="trigger-run-btn">
              <Play className="w-3.5 h-3.5 mr-1" />
              Kör nu
            </Button>
          )}
        </div>
      </div>

      {!canRun && (
        <div className="p-3 text-xs text-muted-foreground bg-muted/30 border-b">
          Process-motor kräver Enterprise-plan. Kontakta support för
          upgrade om du vill köra workflows som processer.
        </div>
      )}

      {error && (
        <div className="p-3 text-xs text-destructive bg-destructive/10 border-b">
          Kunde inte ladda runs: {error}
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {isLoading && runs.length === 0 && (
            <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Laddar runs…
            </div>
          )}
          {!isLoading && runs.length === 0 && (
            <div className="text-center py-8 text-xs text-muted-foreground">
              Inga körningar ännu. Klicka "Kör nu" för att starta första.
            </div>
          )}
          {runs.map((run) => {
            const style = STATUS_STYLES[run.status];
            const StatusIcon = style.icon;
            const TriggerIcon = TRIGGER_ICONS[run.trigger_kind];
            return (
              <div
                key={run.id}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-accent/50 transition-colors text-xs cursor-pointer"
                data-testid={`run-row-${run.id}`}
                onClick={() => setDetailRunId(run.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDetailRunId(run.id); }}
              >
                <Badge variant={style.variant} className="flex-shrink-0 gap-1">
                  <StatusIcon className={`w-3 h-3 ${run.status === 'running' ? 'animate-spin' : ''}`} />
                  {style.label}
                </Badge>
                <TriggerIcon
                  className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"
                  aria-label={run.trigger_kind}
                />
                <span className="text-muted-foreground text-[10px]">
                  {run.trigger_kind}
                </span>
                <span className="flex-1 truncate font-mono text-[10px] text-muted-foreground">
                  {run.id.slice(0, 8)}
                </span>
                <span className="text-muted-foreground text-[10px] tabular-nums">
                  {formatDuration(run.started_at, run.ended_at)}
                </span>
                <span className="text-muted-foreground text-[10px]">
                  {formatRelative(run.started_at)}
                </span>
                {run.error_message && (
                  <span
                    className="text-destructive text-[10px] truncate max-w-[200px]"
                    title={run.error_message}
                  >
                    {run.error_message}
                  </span>
                )}
                {canRun && run.status === 'failed' && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-amber-600"
                    onClick={(e) => { e.stopPropagation(); void handleRetry(run.id); }}
                    title="Retry med samma trigger-data"
                    data-testid={`retry-btn-${run.id}`}
                  >
                    <RotateCcw className="w-3 h-3" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>

      <RunDetailDialog
        open={detailRunId !== null}
        onOpenChange={(o) => { if (!o) setDetailRunId(null); }}
        runId={detailRunId}
      />
    </div>
  );
}
